import { zodResolver } from "@hookform/resolvers/zod";
import { hooks, state } from "@springtree/eva-sdk-react-recoil";
import { Core } from "@springtree/eva-services-core";
import { CoreManagement } from "@springtree/eva-services-core-management";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import classNames from "classnames";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { ChangeEvent, Fragment, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

import CartProductTile from "~/components/cart/cart-product-tile";
import Button from "~/components/common/button";
import HighlightedText from "~/components/common/highlighted-text";
import Icon from "~/components/common/icon";
import myaccount from "~/components/common/myaccount.module.scss";
import UploadImages from "~/components/common/upload-images";
import Checkbox, { CheckboxVariants } from "~/components/form/checkbox";
import InputField from "~/components/form/input-field";
import InputRadioCheckbox from "~/components/form/input-radio-checkbox";
import SelectGeneric from "~/components/form/select-generic";
import orderDetail from "~/components/myaccount/orders/order-details.module.scss";
import { useAuthContext } from "~/contexts/auth";
import useGiftcard from "~/hooks/use-giftcard";
import { useGlobalNotification } from "~/hooks/use-global-notification";
import useSubscriptions from "~/hooks/use-subscriptions";
import Logger from "~/services/logger/logger";
import Constants from "~/utils/constants";
import loyaltyUtils from "~/utils/loyalty-utils";
import productUtils from "~/utils/product-utils";

import styles from "./request-return.module.scss";

type Props = { orderId: number; fromGuest?: boolean };

export default function RequestReturn(props: Props) {
  const t = useTranslations();
  const { user } = useAuthContext();
  const queryClient = useQueryClient();

  const router = useRouter();
  const { hasLoyaltyProgram, userSubscriptions } = useSubscriptions();

  const isUserSubscribedToLoyalty = loyaltyUtils.getLoyaltySubscription(userSubscriptions)?.Status === 2;
  const evaEndpointUrl = hooks.useGetState(state.core.evaEndpointUrlState);

  const getOrder = hooks.useCallService({ service: Core.GetOrder });
  const getReturnableStatus = hooks.useCallService({ service: Core.GetReturnableStatusForOrder });
  const getReturnReasonsByType = hooks.useCallService({ service: CoreManagement.GetReturnReasonsByType });
  const createCustomerReturn = hooks.useCallService({ service: Core.CreateCustomerReturn });
  const storeAndAttachBlobToOrder = hooks.useCallService({ service: Core.StoreAndAttachBlobToOrder });
  const { newErrorGlobalNotification } = useGlobalNotification();

  const [submitError, setSubmitError] = useState<string>();

  const validationSchema = z.object({
    products: z
      .object({
        selected: z.boolean(),
        OrderLineID: z.number(),
        ReturnableQuantity: z.number().min(1).optional(),
        refundReason: z.number().optional(),
        additionalInfo: z.string().optional(),
        images: z.object({ base64: z.string(), type: z.string() }).array().optional(),
      })
      .refine(
        (val) => {
          if (val.selected) {
            return (
              (val.ReturnableQuantity ?? 0) <=
              (querystatus.data
                ? querystatus.data.find((prd) => prd.OrderLineID == val.OrderLineID)!.ReturnableQuantity
                : 0)
            );
          } else {
            return true;
          }
        },
        (val) => ({
          message: `max quantity is ${
            querystatus.data
              ? querystatus.data.find((prd) => prd.OrderLineID == val.OrderLineID)!.ReturnableQuantity
              : 0
          } for selected product`,
          path: ["ReturnableQuantity"],
        })
      )
      .array()
      .refine((arr) => arr.some((line) => line.selected), t("generic.chooseRefoundProducts")),
    refundMethod: z.literal("coupon").or(z.literal("original")),
  });

  type ValidationSchema = z.infer<typeof validationSchema>;

  const {
    register,
    handleSubmit,
    watch,
    clearErrors,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ValidationSchema>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    shouldFocusError: false,
    resolver: zodResolver(validationSchema),
    defaultValues: {
      refundMethod: "original",
    },
  });

  // TODO understand if it's the best approach since it force re-renders on each input change
  const watchproducts = watch("products");

  const onSubmit: SubmitHandler<ValidationSchema> = async (data) => {
    // clear submit error
    setSubmitError(undefined);
    Logger.instance.info("request return submit: ", data);
    const selectedLines = data.products.filter((prd) => prd.selected);

    const createReturnResponse = await createCustomerReturn({
      OrderID: props.orderId,
      CreateInOriginalOrganizationUnit: true,
      ReturnWithoutProducts: false,
      Lines: selectedLines.map((selectedLine) => ({
        OrderLineID: selectedLine.OrderLineID,
        Quantity: selectedLine.ReturnableQuantity!,
        ReturnReasonID: selectedLine.refundReason!,
        CustomFields: {
          additionalInfo: { StringValue: selectedLine.additionalInfo },
        },
      })),
    });

    // if return is successfully created proceed (the place is now handled by eva)
    if (createReturnResponse?.OrderID) {
      // get order detail - used for attaching images (if present) to order lines
      const returnOrder = await getOrder({
        // @ts-expect-error
        OrderID: `${createReturnResponse?.OrderID}`,
        ShowOnlyShippableLines: true,
      });

      const linesWithImages = selectedLines.filter((line) => line.images);

      linesWithImages.map((line) => {
        const returnLines = returnOrder?.Result.Lines.filter((rLine) => rLine.ReturnedOrderLineID == line.OrderLineID);

        returnLines?.map((rLine) => {
          line.images?.map(async (image, index) => {
            await storeAndAttachBlobToOrder({
              Name: `media-${rLine.ID}-${index}`,
              Type: 0,
              Category: "return-image-attachment",
              OriginalName: `media-${rLine.ID}-${index}`,
              Data: image.base64,
              MimeType: image.type,
              OrderLineID: rLine.ID,
            });
          });
        });
      });

      await router.push("/thankyou?order=" + createReturnResponse?.OrderID + "&success=true");
      await queryClient.invalidateQueries(["getReturnableStatus", props.orderId]);
      await queryClient.invalidateQueries(["getLatestOrder", user?.ID]);
    } else if (!createReturnResponse) {
      newErrorGlobalNotification({
        slug: "createReturnFail",
        closeOnClickCta: true,
        ctaCaption: "generic.close",
        onClickCta: () => {
          setSubmitError(undefined);
        },
      });
    } else {
      setSubmitError(createReturnResponse?.Error?.Message);
    }
  };

  const isReturnable = (line: EVA.Core.OrderLineDto) => {
    const product = querystatus.data?.find((el) => el.OrderLineID === line.ID);
    if (product && product.IsReturnable && !productUtils.isAVirtualishOrServiceProduct(line?.Product?.Properties)) {
      return true;
    }
    return false;
  };

  const toggleAll = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      queryorder.data?.products.map((line, index) => {
        if (isReturnable(line)) {
          setValue(`products.${index}.selected`, true);
        }
      });
    } else {
      queryorder.data?.products.map((line, index) => {
        if (isReturnable(line)) {
          setValue(`products.${index}.selected`, false);
        }
      });
    }
  };

  const queryreasons = useQuery({
    queryKey: ["getReturnReasons"],
    staleTime: Infinity,
    queryFn: async () => {
      const data = await getReturnReasonsByType({
        Type: 1,
      });

      if (!data) {
        return null;
      }

      return data.Reasons.filter((reason) => !Constants.EXCLUDED_RETURN_REASONS.includes(reason.BackendID));
    },
    enabled: !!evaEndpointUrl && !!user,
  });

  const queryorder = useQuery({
    queryKey: ["getOrder", props.orderId],
    staleTime: Infinity,
    queryFn: async () => {
      const order = await getOrder({
        OrderID: props.orderId,
        ShowOnlyShippableLines: true,
        AdditionalOrderDataOptions: {
          ProductProperties: ["product_media", "display_value", "product_types"],
        } as EVA.Core.AdditionalOrderDataOptions,
      });

      if (!order) {
        return null;
      }

      return {
        order: order.Result,
        products: order.Result.Lines.filter((line) => !!line.ProductID),
        amounts: order.Amounts,
      };
    },
    enabled: !!evaEndpointUrl && !!user,
  });

  const digitalGiftCardsQuery = useGiftcard().prepareGetDigitalGiftcardOptionsQuery(queryorder?.data?.order);

  const querystatus = useQuery({
    queryKey: ["getReturnableStatus", props.orderId],
    staleTime: Infinity,
    queryFn: async () => {
      const status = await getReturnableStatus({
        // @ts-expect-error
        OrderID: `${props.orderId}`,
      });

      if (!status) {
        return null;
      } else {
        return status.Result;
      }
    },
    enabled: !!evaEndpointUrl && !!user && !!queryorder.data,
  });

  const { allNotReturnable } = useMemo(() => {
    if (queryorder.data) {
      const products = queryorder.data.products;
      return {
        allNotReturnable: products.every((line) => !isReturnable(line)),
      };
    }

    if (querystatus.data) {
      return {
        allNotReturnable: querystatus.data.every((line) => !line.IsReturnable),
      };
    }

    return { allNotReturnable: false };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryorder.data, querystatus.data]);

  if (queryorder.isLoading || querystatus.isLoading) {
    return <></>;
  }

  if (!queryorder.data || queryorder.error) {
    return <p className={myaccount.infoMessage}>{t("generic.noOrderFound", { orderId: props.orderId })}</p>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={orderDetail.orderDetailsWrapper}>
      {props.fromGuest && (
        <div className={orderDetail.centeredTitle}>
          <h2>
            <HighlightedText text={t("generic.yourreturnrequest")} />
          </h2>
        </div>
      )}
      {/* requested in https://kikocosmetics.atlassian.net/browse/UDM-405 */}
      {/* <LinkWrapper
        href={
          hasEvaAuth
            ? `${appRoutes.MYACCOUNT_PURCHASES}/${props.orderId}`
            : appRoutes.MYACCOUNT_PURCHASE_DETAIL + `/${props.orderId}`
        }
        className={orderDetail.orderDetailsTitle}
      >
        <h2>
          <Icon
            name={"chevron-left"}
            aria-hidden
            width={20}
            height={20}
            className={orderDetail.orderDetailsTitleIcon}
          />
          {t("generic.backToOrderDetail")}
        </h2>
      </LinkWrapper> */}

      {hasLoyaltyProgram && isUserSubscribedToLoyalty && (
        <div className={classNames(styles.alertBox, myaccount.myaccountCard)}>
          <Icon name="alert" width={24} height={24} />
          <p className={myaccount.infoMessage}>{t("generic.returns.looseloyalitypoints")}</p>
        </div>
      )}

      <div className={myaccount.myaccountCard}>
        <div className={myaccount.myaccountCardHeader}>
          <h3>{t("generic.chooseRefoundProducts")}</h3>
        </div>
        <div className={myaccount.myaccountCardContent}>
          <Checkbox
            label={t("generic.selectAll")}
            variant={CheckboxVariants.toggle}
            onChange={(e) => {
              toggleAll(e);
              clearErrors();
            }}
            className={styles.selectAll}
            disabled={allNotReturnable}
          />
          <div className={styles.orderItems}>
            {queryorder.data.products?.map((line, index) => {
              // let's assume the return status for the order line is always present
              const returnStatusLine = querystatus.data?.find((el) => `${el.OrderLineID}` === `${line.ID}`)!;
              const watchCurrent = watchproducts?.find((el) => `${el.OrderLineID}` === `${line.ID}`);
              const selected = watchproducts?.some((el) => el?.selected && `${el.OrderLineID}` === `${line.ID}`);
              const isAVirtualishOrServiceProduct = productUtils.isAVirtualishOrServiceProduct(
                line?.Product?.Properties
              );
              const isReturnableStatus = isReturnable(line);

              return (
                <Fragment key={line.ID}>
                  <input
                    {...register(`products.${index}.OrderLineID`, { valueAsNumber: true })}
                    type="hidden"
                    value={returnStatusLine.OrderLineID}
                  />
                  <div className={styles.divider} aria-hidden />
                  {!isReturnableStatus && (
                    <p className={styles.infoBox}>
                      {isAVirtualishOrServiceProduct ? t("generic.returns.notReturnable") : returnStatusLine.Reason}
                    </p>
                  )}
                  <div className={classNames(styles.customLabel, { [styles.disabled]: !isReturnableStatus })}>
                    {isReturnableStatus ? (
                      <>
                        <InputRadioCheckbox
                          type="checkbox"
                          {...register(`products.${index}.selected`, {
                            onChange: () => {
                              clearErrors();
                            },
                          })}
                          error={errors.products?.[index]?.message}
                          onClearErrors={() => clearErrors(`products.${index}`)}
                        />
                        <input
                          type="hidden"
                          {...register(`products.${index}.ReturnableQuantity`, { valueAsNumber: true })}
                          defaultValue={returnStatusLine?.ReturnableQuantity}
                        />
                      </>
                    ) : (
                      <>
                        <input type="hidden" {...register(`products.${index}.selected`, { value: false })} />
                        <Icon name="uneditable" width={16} height={16} />
                      </>
                    )}
                    <CartProductTile
                      line={line}
                      quantity={watchCurrent?.ReturnableQuantity}
                      disableAdd={!((watchCurrent?.ReturnableQuantity ?? 0) < returnStatusLine?.ReturnableQuantity)}
                      disableRemove={!((watchCurrent?.ReturnableQuantity ?? 0) > 1)}
                      toggleQuantity={(removing) => {
                        if (removing) {
                          setValue(`products.${index}.ReturnableQuantity`, (watchCurrent?.ReturnableQuantity ?? 0) - 1);
                        } else {
                          setValue(`products.${index}.ReturnableQuantity`, (watchCurrent?.ReturnableQuantity ?? 0) + 1);
                        }
                      }}
                      disableCartQty={!selected}
                      disableCartActions
                      className={styles.tile}
                      showNetPriceWithoutPromo
                      giftCardData={digitalGiftCardsQuery.data?.find(
                        (card) => card.Identifiers.OrderLineID === line.ID
                      )}
                    />
                  </div>
                  {/* and selected */}
                  {selected && (
                    <>
                      <SelectGeneric
                        {...register(`products.${index}.refundReason`, { valueAsNumber: true })}
                        label={t("generic.returns.why")}
                        options={queryreasons.data!.map((res) => ({ value: res.ID, label: res.Name }))}
                        className={styles.selectReason}
                      />
                      <UploadImages setImages={(images) => setValue(`products.${index}.images`, images)} />
                      <p className={styles.reg}>{t("generic.returns.tellmore")}</p>
                      <InputField
                        {...register(`products.${index}.additionalInfo`)}
                        className={styles.additionalInfo}
                        label={t("generic.returns.additional")}
                        isOptional
                      />
                    </>
                  )}
                </Fragment>
              );
            })}
            {errors.products && (
              <p className={styles.errorMessage}>{errors.products?.message ?? errors.products?.root?.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* TODO show this section when an actual choice could be made */}
      {false && (
        <div className={myaccount.myaccountCard}>
          <div className={myaccount.myaccountCardHeader}>
            <h3>{t("generic.returns.choserefundmethod")}</h3>
          </div>
          <div className={classNames(myaccount.myaccountCardContent, styles.refundList)}>
            <InputRadioCheckbox {...register("refundMethod")} type="radio" value="coupon" disabled>
              <p className={styles.bold}>{t("generic.returns.refundwithcoupon")}</p>
              <p className={styles.reg}>{t("generic.returns.refundwithcoupondesc")}</p>
            </InputRadioCheckbox>
            <InputRadioCheckbox {...register("refundMethod")} type="radio" value="original">
              <p className={styles.bold}>{t("generic.returns.refundwithoriginal")}</p>
              {/* TODO understand how to handle properly the card number */}
              <p className={styles.reg}>Mastercard ending 1234</p>
            </InputRadioCheckbox>
            {errors.refundMethod && <p className={styles.errorMessage}>{errors.refundMethod?.message}</p>}
          </div>
        </div>
      )}

      {submitError && <p className={styles.errorMessage}>{submitError}</p>}
      <Button
        variant="primary"
        type="submit"
        disabled={allNotReturnable || isSubmitting}
        loading={isSubmitting}
        className={styles.centeredButton}
      >
        {t("generic.returns.send")}
      </Button>

      {/* requested in https://kikocosmetics.atlassian.net/browse/UDM-405 */}
      {/* <p className={myaccount.infoMessage}>{t("generic.returns.pointsdeduction")}</p> */}
    </form>
  );
}
